import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Avatar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  CircularProgress,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { format, parse } from 'date-fns'
import {
  getMockBatchById,
  ProviderPayout,
  ProviderPayoutStatus,
  formatCurrency,
  getInitials,
  PaymentBatch,
} from './mockData'
import {
  getBatchById,
  updateBatchStatus,
  getBatchCreatedByName,
  exportBatch,
  removeBatchItem,
  BatchDetailResponse,
} from '@/services/batchService'
import ProcessPaymentModal from './components/ProcessPaymentModal'
import ProviderBatchDetailDrawer from './components/ProviderBatchDetailDrawer'

const formatMonth = (batchMonth: string): string => {
  try {
    return format(parse(batchMonth, 'yyyy-MM', new Date()), 'MMMM yyyy')
  } catch {
    return batchMonth
  }
}

const safeDate = (value?: string): string => {
  if (!value) return '—'
  try {
    return format(new Date(value), 'MM/dd/yyyy')
  } catch {
    return value
  }
}

const statusChipSx = (status: string) => {
  switch (status.toLowerCase()) {
    case 'paid':
      return { bgcolor: '#dcfce7', color: '#166534' }
    case 'cancelled':
      return { bgcolor: '#fee2e2', color: '#b91c1c' }
    case 'processing':
      return { bgcolor: '#e0f2fe', color: '#0369a1' }
    default:
      return { bgcolor: '#fef3c7', color: '#92400e' }
  }
}

const InfoItem: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, letterSpacing: '0.04em', color: '#64748b' }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
)

// Map the single-batch API response into the PaymentBatch shape.
const mapBatchDetail = (data: BatchDetailResponse['data']): PaymentBatch => {
  const b = data.batch
  const isClaim = b.batchType === 'claim'
  const providers: ProviderPayout[] = (data.batchDetails || []).map(d => {
    // Claim batches carry the line items in claimIds instead of invoiceIds.
    const lineItems = isClaim ? d.claimIds || [] : d.invoiceIds || []
    return {
      providerId: d.providerId?._id || d._id,
      name: `${d.providerId?.firstName || ''} ${d.providerId?.lastName || ''}`.trim() || 'N/A',
      role: '',
      invoiceCount: lineItems.length,
      sessions: d.sessionIds?.length || 0,
      payableAmount: d.providerPayout ?? 0,
      paymentMethod: '',
      status: (d.status as ProviderPayoutStatus) || 'Pending',
      stripeStatus: 'Connected',
      invoices: lineItems.map(item =>
        typeof item === 'string'
          ? { invoice_ids: item, amount: 0 }
          : {
              _id: item._id,
              invoice_ids: isClaim
                ? item.claimId || item._id
                : item.invoice_ids || item._id,
              amount: item.providerPayout ?? item.totalAmount ?? 0,
            }
      ),
    }
  })
  return {
    _id: b._id,
    name: b.batchName,
    month: formatMonth(b.batchMonth),
    description: b.description,
    createdBy: getBatchCreatedByName(b.createdBy),
    createdDate: b.createdAt,
    paymentDate: b.paymentDate,
    status: b.status as PaymentBatch['status'],
    totalProviders: b.totalProvider,
    totalInvoices: isClaim ? b.totalClaim ?? 0 : b.totalInvoice,
    totalSessions: data.totalSessions ?? 0,
    totalAmount: b.totalAmount,
    totalPayout: b.providerPayout ?? 0,
    paymentMethod: '',
    providers,
    batchType: b.batchType,
    timeline: [{ label: 'Created', timestamp: b.createdAt }],
  }
}

const StatCard: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Card sx={{ p: 2.5, borderRadius: 2, boxShadow: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
      {value}
    </Typography>
  </Card>
)

const AgencyBatchDetailPage: React.FC = () => {
  const { batchId } = useParams<{ batchId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const stateBatch = (location.state as { batch?: PaymentBatch } | null)?.batch
  const [batch, setBatch] = useState<PaymentBatch | undefined>(
    () => stateBatch || getMockBatchById(batchId || '')
  )
  const [loading, setLoading] = useState(true)
  const [processOpen, setProcessOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [drawerProvider, setDrawerProvider] = useState<ProviderPayout | null>(
    null
  )

  const handleExport = async () => {
    if (!batchId) return
    setExporting(true)
    try {
      await exportBatch(batchId, `${batch?.name || 'batch'}.csv`)
    } catch (err) {
      console.error('Error exporting batch:', err)
    } finally {
      setExporting(false)
    }
  }

  const loadBatch = useCallback(async () => {
    if (!batchId) return
    setLoading(true)
    try {
      const res = await getBatchById(batchId)
      setBatch(mapBatchDetail(res.data))
    } catch (err) {
      console.error('Error fetching batch detail:', err)
    } finally {
      setLoading(false)
    }
  }, [batchId])

  const handleRemoveInvoice = async (itemId: string) => {
    if (!batchId || !batch) return
    await removeBatchItem(batchId, itemId, batch.batchType || 'invoice')
    // Refetch and keep the drawer in sync with the refreshed provider data.
    const res = await getBatchById(batchId)
    const mapped = mapBatchDetail(res.data)
    setBatch(mapped)
    setDrawerProvider(prev => {
      if (!prev) return prev
      const refreshed = mapped.providers.find(
        p => p.providerId === prev.providerId
      )
      // Close the drawer if the provider no longer has any line items.
      return refreshed && refreshed.invoices.length > 0 ? refreshed : null
    })
  }

  useEffect(() => {
    loadBatch()
  }, [loadBatch])

  if (loading && !batch) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!batch) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="text.secondary">
          Batch not found.
        </Typography>
      </Box>
    )
  }

  const handleProcess = async (status: string) => {
    if (!batchId) return
    try {
      await updateBatchStatus(batchId, status)
      // Refetch so the table + status reflect the new payout status.
      await loadBatch()
    } catch (err) {
      console.error('Error updating batch status:', err)
    } finally {
      setProcessOpen(false)
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button
        onClick={() => navigate('/agency-payment-batches')}
        startIcon={<ArrowBackIcon />}
        disableRipple
        disableElevation
        sx={{
          textTransform: 'none',
          color: '#7A6FA1',
          px: 0,
          mb: 1.5,
          bgcolor: 'transparent',
          boxShadow: 'none',
          '&:hover, &:focus, &:active, &.Mui-focusVisible': {
            bgcolor: 'transparent',
            boxShadow: 'none',
            textDecoration: 'none',
          },
        }}
      >
        Back to Payout Batches
      </Button>

      {/* Header card */}
      <Card
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: 1,
          mb: 3,
          borderTop: '4px solid #7A6FA1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <Chip
                label={batch.status}
                size="small"
                sx={{ ...statusChipSx(batch.status), fontWeight: 600 }}
              />
              <Chip
                label={batch.batchType === 'claim' ? 'Claim' : 'Invoice'}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500, borderColor: '#c7d2fe', color: '#4f46e5' }}
              />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
              {batch.name}
            </Typography>
            {batch.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {batch.description}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 3 },
                mt: 2,
              }}
            >
              <InfoItem label="Batch Month" value={batch.month} />
              <InfoItem label="Created At" value={safeDate(batch.createdDate)} />
              <InfoItem
                label="Total Batch Amount"
                value={formatCurrency(batch.totalAmount)}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleExport}
              disabled={exporting}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                borderColor: '#7A6FA1',
                color: '#7A6FA1',
              }}
            >
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setProcessOpen(true)}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: '#7A6FA1',
                '&:hover': { bgcolor: '#655a8a' },
              }}
            >
              Update pay status
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Summary stats */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        <StatCard label="Total Providers" value={String(batch.totalProviders)} />
        <StatCard label="Total Sessions" value={String(batch.totalSessions)} />
        <StatCard
          label={batch.batchType === 'claim' ? 'Total Claims' : 'Total Invoices'}
          value={String(batch.totalInvoices)}
        />
        <StatCard
          label="Total Provider Payout"
          value={formatCurrency(batch.totalPayout)}
        />
      </Box>

      {/* Provider table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#7A6FA1' }}>
              {[
                'Provider',
                batch.batchType === 'claim' ? 'Claim Count' : 'Invoice Count',
                'Sessions',
                'Provider Payout',
                'Status',
                'Action',
              ].map((head, idx) => (
                <TableCell
                  key={head}
                  sx={{
                    color: 'white',
                    fontWeight: 600,
                    textAlign: idx === 5 ? 'right' : 'left',
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {batch.providers.map(provider => (
              <TableRow key={provider.providerId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 36,
                        height: 36,
                        fontSize: 13,
                        bgcolor: '#ede9fe',
                        color: '#7A6FA1',
                      }}
                    >
                      {getInitials(provider.name)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {provider.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {provider.role}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>{provider.invoiceCount}</TableCell>
                <TableCell>{provider.sessions}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>
                  {formatCurrency(provider.payableAmount)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={provider.status}
                    size="small"
                    sx={
                      provider.status === 'Paid'
                        ? { bgcolor: '#dcfce7', color: '#166534', fontWeight: 500 }
                        : { bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }
                    }
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'right' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setDrawerProvider(provider)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      bgcolor: '#7A6FA1',
                      '&:hover': { bgcolor: '#655a8a' },
                    }}
                  >
                    View Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ProcessPaymentModal
        open={processOpen}
        onClose={() => setProcessOpen(false)}
        batch={batch}
        onProcess={handleProcess}
      />

      <ProviderBatchDetailDrawer
        open={Boolean(drawerProvider)}
        onClose={() => setDrawerProvider(null)}
        batch={batch}
        provider={drawerProvider}
        onRemoveInvoice={handleRemoveInvoice}
      />
    </Box>
  )
}

export default AgencyBatchDetailPage
